/**
 * taskListShared — Constantes e componentes compartilhados pela tabela
 * de tarefas (listagem padrão + visualização agrupada).
 *
 * Centraliza o que era originalmente exportado de `TaskList.tsx` para
 * quebrar a dependência circular com `TaskListTable` e `TaskGroupedView`.
 *
 * @version 2.0.0
 * RELEASE: 2026-10-09 v2.0.0 - PhaseDropdown lista TODAS as fases agrupadas por
 *          categoria, filtradas por permissão; reutilizado também pela OS.
 * RELEASE: 2026-06-24 v1.0.0 - Extraído de TaskList.tsx para reuso na
 *          visualização agrupada (mesma table, mesmos componentes).
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronDown, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TaskListShared');

/** Cores da barra lateral de prioridade. */
export const PRIORITY_BAR_COLORS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-muted-foreground/30',
};

/** Labels traduzidos de status de tarefa. */
export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  paused: 'Pausada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

/** Estilos pill (fundo tonalizado + ponto colorido) para cada status. */
export const STATUS_STYLES: Record<string, string> = {
  pending:
    'inline-flex items-center rounded-full font-medium bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-slate-500 before:mr-1.5',
  in_progress:
    'inline-flex items-center rounded-full font-medium bg-blue-50 text-blue-700 hover:bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-blue-500 before:mr-1.5',
  paused:
    'inline-flex items-center rounded-full font-medium bg-amber-50 text-amber-800 hover:bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-amber-500 before:mr-1.5',
  completed:
    'inline-flex items-center rounded-full font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-emerald-500 before:mr-1.5',
  cancelled:
    'inline-flex items-center rounded-full font-medium bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-rose-500 before:mr-1.5',
};

/** Formata data de prazo curto (dd/MM/yy) ou retorna travessão. */
export const formatDue = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return format(new Date(dateStr), 'dd/MM/yy', { locale: ptBR });
};

/** Grupo de fases por categoria exibido no seletor. */
export interface PhaseGroup {
  id: string;
  name: string;
  color: string;
  phases: Array<{ id: string; name: string; color: string }>;
}

/**
 * Agrupa fases por categoria (função pura, testável).
 * - Ignora categorias/fases inativas;
 * - Mantém apenas fases permitidas (`canMove`), exceto a fase atual (contexto);
 * - Oculta categorias sem fases visíveis.
 */
export function groupPhasesByCategory(
  categories: Array<{ id: string; name: string; color: string; sort_order?: number; is_active?: boolean }>,
  phases: Array<{ id: string; name: string; color: string; category_id: string; sort_order?: number; is_active?: boolean }>,
  canMove: (phaseId: string) => boolean,
  currentPhaseId?: string | null,
): PhaseGroup[] {
  return [...categories]
    .filter((c) => c.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((c) => ({
      id: c.id,
      name: c.name,
      color: c.color,
      phases: phases
        .filter((p) => p.category_id === c.id && p.is_active !== false)
        .filter((p) => p.id === currentPhaseId || canMove(p.id))
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((p) => ({ id: p.id, name: p.name, color: p.color })),
    }))
    .filter((g) => g.phases.length > 0);
}

/** Dados mínimos de exibição da fase (Tarefas/Retenção/Jurídico/OS). */
export interface PhaseDropdownItem {
  id: string;
  phase_id: string | null;
  phase_name: string | null;
  phase_color: string | null;
  phase_sort_order: number;
  total_category_phases: number;
}

/**
 * PhaseDropdown — Seletor inline de fase da listagem (padrão Retenção).
 * v2.0.0: lista TODAS as fases do módulo agrupadas por categoria, somente
 * as que o usuário pode mover (`canMoveToPhase`). A validação real
 * permanece no servidor (move_task_phase / move_service_order_phase).
 */
export function PhaseDropdown({
  task,
  disabled = false,
  canMoveToPhase,
  onMove,
}: {
  task: PhaseDropdownItem;
  disabled?: boolean;
  /** Permissão por fase de destino; ausente = todas permitidas */
  canMoveToPhase?: (phaseId: string) => boolean;
  /** Movimento customizado (ex.: OS). Padrão: move_task_phase */
  onMove?: (phaseId: string, categoryId: string) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Catálogos derivados da rota (Tarefas/Retenção/Jurídico/OS) — cache compartilhado
  const { categories, isLoading: loadingCats } = useTaskCategories(true);
  const { phases, isLoading: loadingPhases } = useTaskPhases();

  const groups = useMemo(
    () => groupPhasesByCategory(
      (categories || []) as any[],
      (phases || []) as any[],
      canMoveToPhase ?? (() => true),
      task.phase_id,
    ),
    [categories, phases, canMoveToPhase, task.phase_id],
  );

  /** Altera a fase (troca a categoria junto quando a fase é de outra) */
  const handleChangePhase = async (phaseId: string, categoryId: string) => {
    if (phaseId === task.phase_id) return;
    setOpen(false);
    try {
      if (onMove) {
        await onMove(phaseId, categoryId);
      } else {
        const { error } = await supabase.rpc('move_task_phase', {
          p_task_id: task.id,
          p_new_phase_id: phaseId,
        });
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
        queryClient.invalidateQueries({ queryKey: ['task-list-kpis'] });
      }
      toast.success('Fase alterada com sucesso');
    } catch (err: any) {
      logger.error('Erro ao alterar fase', err);
      toast.error(`Erro ao alterar fase: ${err?.message ?? 'falha'}`);
    }
  };

  // Barra de progresso dentro da categoria atual
  const progressBar = (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 mt-0.5">
      <div
        className="h-full rounded-full transition-all"
        style={{
          width: `${task.total_category_phases > 0 ? Math.max(10, (task.phase_sort_order / task.total_category_phases) * 100) : 10}%`,
          backgroundColor: task.phase_color ?? undefined,
        }}
      />
    </div>
  );

  // Versão somente leitura (sem permissão de troca)
  if (disabled) {
    return (
      <div className="min-w-0 max-w-[140px] px-1 py-0.5">
        <span className="text-xs text-foreground truncate block">
          {task.phase_name || 'Sem fase'}
        </span>
        {progressBar}
      </div>
    );
  }

  const loading = loadingCats || loadingPhases;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div className="group/phase cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors min-w-0 max-w-[140px]">
          <div className="flex items-center gap-1">
            <span className="text-xs text-foreground truncate block">
              {task.phase_name || 'Sem fase'}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover/phase:opacity-100 transition-opacity shrink-0" />
          </div>
          {progressBar}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="px-1 pb-2 text-xs font-medium text-muted-foreground">Selecione a nova fase</p>
        {/* Rolagem nativa (padrão do projeto para listas em popover) */}
        <div className="max-h-80 overflow-y-auto overscroll-contain space-y-2 pr-0.5">
          {loading ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">Carregando...</div>
          ) : groups.length === 0 ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">Nenhuma fase disponível</div>
          ) : (
            groups.map((g) => (
              <div key={g.id} className="space-y-0.5">
                {/* Cabeçalho da categoria com faixa lateral colorida */}
                <div
                  className="flex items-center gap-1.5 rounded-md border-l-[3px] bg-muted/50 px-2 py-1.5"
                  style={{ borderLeftColor: g.color }}
                >
                  <FolderKanban className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</span>
                </div>
                {g.phases.map((phase) => {
                  const isCurrent = phase.id === task.phase_id;
                  return (
                    <button
                      key={phase.id}
                      type="button"
                      disabled={isCurrent}
                      onClick={() => handleChangePhase(phase.id, g.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-accent',
                        isCurrent && 'bg-accent font-medium ring-1 ring-border cursor-default',
                      )}
                    >
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
                      <span className="truncate">{phase.name}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
